import { Formik } from 'formik';

import { isMobile } from 'hooks';
import { ButtonColors } from 'types';
import { Icon } from 'components/icon';
import * as CommonStyles from 'components/styles';
import { dayToDate, longRange } from 'utils/time-off';
import { Dialog, DialogType } from 'components/dialog';
import { InputField, DatePickerField } from 'components/fields';
import { Button, ButtonTypes, ButtonWithRef } from 'components/button';

import { TimeOffDecisionDialogProps } from './types';
import { fullRange, isSomeDays, pickedDays } from './config';

// TIME OFF (owner 24 Oct 2026; plan §6.2): the middle icon, "Approve some
// days" -- a shorter range inside the dates asked for (the time-off
// "Partial Approve").
export const SomeDaysDialog = ({ row, decide }: TimeOffDecisionDialogProps) => {
  const mobile = isMobile();

  return (
    <Dialog
      title="Approve some days"
      closeOnDocumentClick={false}
      trigger={
        <ButtonWithRef
          fullWidth={mobile}
          tooltip="Approve some days"
          icon={<Icon.CalendarCheck />}
          colorType={ButtonColors.LightYellow}
        />
      }
    >
      {(close, _, ButtonWrap) => (
        <Formik
          initialValues={{ note: '', days: fullRange(row) }}
          onSubmit={async ({ note, days }) => {
            const { endDate, startDate } = pickedDays(days);
            const result = await decide(
              {
                approvedEndDate: endDate,
                note: note.trim() || null,
                approvedStartDate: startDate,
                decision: 'approvedSomeDays',
              },
              'approvedSomeDays'
            );
            if (result) {
              close();
            }
          }}
        >
          {({ values, isSubmitting }) => {
            const valid = isSomeDays(row, values.days);
            const picked = pickedDays(values.days);
            return (
              <CommonStyles.Form gap={24}>
                <p>
                  {row.name} asked for{' '}
                  <span className="semibold">
                    {longRange(row.startDate, row.endDate, { dropYear: true })}
                  </span>
                  . Pick the days you approve.
                </p>

                <DatePickerField
                  name="days"
                  selectsRange
                  // The pop-up focuses this field on open; keep the calendar shut
                  // until it is clicked, so it doesn't cover the form (Dev pass 24 Oct).
                  preventOpenOnFocus
                  label="Approved days"
                  placeholderText="Approved days"
                  maxDate={dayToDate(row.endDate) ?? undefined}
                  minDate={dayToDate(row.startDate) ?? undefined}
                />
                {/* Only once the approver changed the range (on open it is
                    the full request; Dev pass 24 Oct). */}
                {!valid &&
                  picked.startDate &&
                  picked.endDate &&
                  (picked.startDate !== row.startDate ||
                    picked.endDate !== row.endDate) && (
                    <p>
                      Pick fewer days than asked, inside the requested dates
                    </p>
                  )}

                <InputField
                  name="note"
                  type="textarea"
                  maxLength={200}
                  label="Note to the rep (optional)"
                  placeholder="e.g. Need you back for the 14th"
                />

                <ButtonWrap type={DialogType.Plain}>
                  <Button
                    fullWidth
                    text="Cancel"
                    onClick={close}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    fullWidth
                    text="Approve"
                    type={ButtonTypes.Submit}
                    disabled={!valid || isSubmitting}
                    colorType={ButtonColors.LightGreen}
                  />
                </ButtonWrap>
              </CommonStyles.Form>
            );
          }}
        </Formik>
      )}
    </Dialog>
  );
};
